import { authFetch, API_URL, clearAuthSession, getAuthHeaders } from "../../utils/auth";

const MENU_ITEMS_URL = `${API_URL}/menu-items/`;
const MENU_CATEGORIES_URL = `${API_URL}/menu-categories/`;

function getJsonAuthHeaders() {
  return {
    Accept: "application/json",
    ...getAuthHeaders(),
  };
}

function getJsonWriteHeaders() {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...getAuthHeaders(),
  };
}

function getFormDataAuthHeaders() {
  const headers = {
    Accept: "application/json",
    ...getAuthHeaders(),
  };

  // FormData must set its own multipart boundary.
  // Never send application/json for FormData requests.
  delete headers["Content-Type"];
  delete headers["content-type"];

  return headers;
}

function getFirstValidationError(data) {
  if (!data || typeof data !== "object") {
    return "";
  }

  for (const [field, value] of Object.entries(data)) {
    if (Array.isArray(value) && value.length > 0) {
      const firstValue = value[0];

      if (
        firstValue &&
        typeof firstValue === "object"
      ) {
        const nestedError =
          getFirstValidationError(firstValue);

        if (nestedError) {
          return `${field}: ${nestedError}`;
        }
      }

      return `${field}: ${String(firstValue)}`;
    }

    if (
      value &&
      typeof value === "object"
    ) {
      const nestedError =
        getFirstValidationError(value);

      if (nestedError) {
        return `${field}: ${nestedError}`;
      }
    }

    if (typeof value === "string") {
      return `${field}: ${value}`;
    }
  }

  return "";
}

async function parseResponse(response) {
  const contentType =
    response.headers.get("content-type") || "";

  let data = null;

  if (contentType.includes("application/json")) {
    data = await response.json();
  } else {
    await response.text();
    data = { detail: "The request could not be completed." };
  }

  if (response.status === 401) {
    clearAuthSession();

    const error = new Error(
      data?.detail ||
        "Your session has expired. Please sign in again.",
    );

    error.status = 401;
    error.data = data;

    throw error;
  }

  if (!response.ok) {
    const validationError =
      getFirstValidationError(data);

    const error = new Error(
      data?.detail ||
        data?.message ||
        validationError ||
        "The request could not be completed.",
    );

    error.status = response.status;
    error.data = data;

    throw error;
  }

  return data;
}

function normalizeListResponse(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  return [];
}

export async function fetchMenuItem(itemId, { signal } = {}) {
  if (!itemId) {
    throw new Error(
      "A menu item ID is required.",
    );
  }

  const response = await authFetch(
    `${MENU_ITEMS_URL}${itemId}/`,
    {
      method: "GET",
      headers: getJsonAuthHeaders(),
      signal,
    },
  );

  return parseResponse(response);
}

export async function fetchMenuItems({
  search = "",
  category = "",
  active = "",
  available = "",
  featured = "",
  seasonal = "",
  ordering = "display_order",
  signal,
} = {}) {
  const params = new URLSearchParams();

  if (search) {
    params.set("search", search);
  }

  if (category) {
    params.set("category", category);
  }

  if (active !== "") {
    params.set("active", active);
  }

  if (available !== "") {
    params.set("available", available);
  }

  if (featured !== "") {
    params.set("featured", featured);
  }

  if (seasonal !== "") {
    params.set("seasonal", seasonal);
  }

  if (ordering) {
    params.set("ordering", ordering);
  }

  const queryString = params.toString();

  const url = queryString
    ? `${MENU_ITEMS_URL}?${queryString}`
    : MENU_ITEMS_URL;

  const response = await authFetch(url, {
    method: "GET",
    headers: getJsonAuthHeaders(),
    signal,
  });

  const data = await parseResponse(response);

  return normalizeListResponse(data);
}

export async function fetchMenuCategories({
  signal,
} = {}) {
  const response = await authFetch(
    MENU_CATEGORIES_URL,
    {
      method: "GET",
      headers: getJsonAuthHeaders(),
      signal,
    },
  );

  const data = await parseResponse(response);

  return normalizeListResponse(data);
}

export async function createMenuItem(formData) {
  if (!(formData instanceof FormData)) {
    throw new TypeError(
      "createMenuItem expected a FormData payload.",
    );
  }

  const response = await authFetch(
    MENU_ITEMS_URL,
    {
      method: "POST",
      headers: getFormDataAuthHeaders(),
      body: formData,
    },
  );

  return parseResponse(response);
}

export async function updateMenuItem(
  itemId,
  formData,
) {
  if (!itemId) {
    throw new Error(
      "A menu item ID is required.",
    );
  }

  if (!(formData instanceof FormData)) {
    throw new TypeError(
      "updateMenuItem expected a FormData payload.",
    );
  }

  const response = await authFetch(
    `${MENU_ITEMS_URL}${itemId}/`,
    {
      method: "PATCH",
      headers: getFormDataAuthHeaders(),
      body: formData,
    },
  );

  return parseResponse(response);
}

export async function deleteMenuItem(itemId) {
  const response = await authFetch(
    `${MENU_ITEMS_URL}${itemId}/`,
    {
      method: "DELETE",
      headers: getJsonAuthHeaders(),
    },
  );

  if (response.status === 204) {
    return {
      success: true,
    };
  }

  return parseResponse(response);
}

export async function toggleMenuItemActive(itemId) {
  const response = await authFetch(
    `${MENU_ITEMS_URL}${itemId}/toggle-active/`,
    {
      method: "PATCH",
      headers: getJsonAuthHeaders(),
    },
  );

  return parseResponse(response);
}

export async function toggleMenuItemAvailable(itemId) {
  const response = await authFetch(
    `${MENU_ITEMS_URL}${itemId}/toggle-available/`,
    {
      method: "PATCH",
      headers: getJsonAuthHeaders(),
    },
  );

  return parseResponse(response);
}

export async function toggleMenuItemFeatured(itemId) {
  const response = await authFetch(
    `${MENU_ITEMS_URL}${itemId}/toggle-featured/`,
    {
      method: "PATCH",
      headers: getJsonAuthHeaders(),
    },
  );

  return parseResponse(response);
}

async function syncMenuItemResource(
  itemId,
  resourcePath,
  payloadKey,
  records,
) {
  if (!itemId) {
    throw new Error(
      "A menu item ID is required.",
    );
  }

  const response = await authFetch(
    `${MENU_ITEMS_URL}${itemId}/${resourcePath}/sync/`,
    {
      method: "PUT",
      headers: getJsonWriteHeaders(),
      body: JSON.stringify({
        [payloadKey]: records,
      }),
    },
  );

  return parseResponse(response);
}

export function syncMenuItemVariants(itemId, variants) {
  return syncMenuItemResource(
    itemId,
    "variants",
    "variants",
    variants,
  );
}

export function syncMenuItemFlavors(itemId, flavors) {
  return syncMenuItemResource(
    itemId,
    "flavors",
    "flavors",
    flavors,
  );
}

export function syncMenuItemAddOns(itemId, addOns) {
  return syncMenuItemResource(
    itemId,
    "add-ons",
    "add_ons",
    addOns,
  );
}
